import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@alfresco/adf-core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { SpSharedModule } from '../../shared/sp-shared.module';
import { WfTaskApi } from '../services/wf-task-api.service';
import { WfSubTaskApi } from '../services/wf-sub-task-api.service';
import { TaskSummary, WfSubTask, MyTasksColumn } from '../services/wf-runtime-types';
import { WfSubTasksPanelComponent } from '../components/wf-sub-tasks-panel.component';

/** A task row enriched for the unified inbox. */
interface WorkItem extends TaskSummary {
  kind: 'me' | 'group';     // assigned to me vs claimable by my groups
  dueMs: number | null;     // due date as epoch ms (null = no due date)
  /** R2.3 — 'task' (Flowable task) or 'consult' (a consultation assigned to me). */
  itemType?: 'task' | 'consult';
  /** R2.3 — the backing consultation when itemType === 'consult'. */
  consult?: WfSubTask;
}

/**
 * #MyWork — a single cross-workflow inbox, rendered as its own top-level page
 * (NOT inside the Workflows shell, so there is no per-workflow sidebar).
 *
 * <p>Lists every task that needs the current user regardless of workflow, by
 * calling {@code /tasks/my} (assigned to me) and {@code /tasks/claimable}
 * (my groups) WITHOUT a {@code wfKey} filter. Presented as a flat table
 * (workflow / ref / subject / holder / priority / age) the way a triage queue
 * reads, so the user can work straight down the list.</p>
 *
 * <p>Opening a task routes to the existing task detail with
 * {@code ?return=mywork}; on completion the detail routes back here and the
 * finished task drops off. "Back" returns to this page or the dashboard,
 * never into the workflow shell.</p>
 */
@Component({
  standalone: true,
  selector: 'wf-my-work',
  imports: [CommonModule, FormsModule, SpSharedModule, WfSubTasksPanelComponent],
  template: `
    <div class="mw-page">
      <sp-header></sp-header>

      <main class="mw-main">
        <div class="mw-head">
          <div class="mw-title-wrap">
            <h1 class="mw-h1">My work</h1>
            <span class="mw-count" *ngIf="!loading">{{ filtered().length }} tasks need you</span>
          </div>
          <button class="mw-refresh" *ngIf="canConfigureColumns" (click)="configureColumns()" title="Configure columns">
            <span class="material-icons">view_column</span>
          </button>
          <button class="mw-refresh" (click)="load()" [disabled]="loading" title="Refresh">
            <span class="material-icons">refresh</span>
          </button>
        </div>


        <div class="mw-controls">
          <div class="mw-search">
            <span class="material-icons">search</span>
            <input [(ngModel)]="search" (ngModelChange)="page=0" placeholder="Search tasks">
          </div>
          <select [(ngModel)]="wfFilter" (ngModelChange)="page=0">
            <option value="">All workflows</option>
            <option *ngFor="let w of workflowOptions" [value]="w.key">{{ w.label }}</option>
          </select>
          <select [(ngModel)]="sort" (ngModelChange)="page=0">
            <option value="recent">Sort: most recent</option>
            <option value="due">Sort: due date</option>
            <option value="age">Sort: oldest first</option>
            <option value="prio">Sort: priority</option>
          </select>
        </div>

        <div class="mw-loading" *ngIf="loading">
          <span class="material-icons spin">progress_activity</span> Loading your tasks…
        </div>
        <div class="mw-error" *ngIf="error && !loading">{{ error }}</div>

        <div class="mw-card" *ngIf="!loading && !error">
          <div class="mw-scroll">
          <div class="mw-empty" *ngIf="!filtered().length">
            <span class="material-icons">inbox</span>
            <p>Nothing in your queue right now.</p>
          </div>

          <table class="mw-table" *ngIf="filtered().length">
            <thead>
              <tr>
                <th *ngFor="let col of columns" [ngClass]="colClass(col)" [style.width]="col.width || null">{{ col.label }}</th>
                <th class="c-act"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let t of pageItems()" (click)="open(t)">
                <td *ngFor="let col of columns" [ngClass]="colClass(col)" [ngSwitch]="col.render || 'text'">
                  <!-- Workflow badge -->
                  <span *ngSwitchCase="'workflow'" class="mw-badge"
                        [style.background]="wfStyle(t).bg" [style.color]="wfStyle(t).fg">
                    <span class="mw-dot" [style.background]="wfStyle(t).dot"></span>
                    <span class="mw-ellip">{{ wfLabel(t) }}</span>
                  </span>
                  <!-- Reference -->
                  <span *ngSwitchCase="'ref'" class="mw-ref">{{ cellText(t, col) || '—' }}</span>
                  <!-- Holder -->
                  <ng-container *ngSwitchCase="'holder'">
                    <span class="mw-avatar" [class.group]="t.kind==='group'">{{ initials(t) }}</span>
                    <span class="mw-holder-label">{{ t.kind==='me' ? 'me' : 'group' }}</span>
                  </ng-container>
                  <!-- Age -->
                  <span *ngSwitchCase="'age'" [class.overdue]="t.overdue">{{ cellText(t, col) || '—' }}</span>
                  <!-- Status/step badge -->
                  <span *ngSwitchCase="'statusBadge'" class="mw-step-badge">{{ cellText(t, col) || '—' }}</span>
                  <!-- Text (default). The Subject column also shows the consult badge + sub-step line. -->
                  <ng-container *ngSwitchDefault>
                    <ng-container *ngIf="isSubjectCol(col); else plainText">
                      <div class="mw-subject-row">
                        <span class="mw-subject mw-ellip">{{ subject(t) }}</span>
                        <span class="mw-consult-badge" *ngIf="t.awaitingConsult" title="Awaiting a consultation response">
                          <span class="material-icons">forum</span> Awaiting consultation
                        </span>
                      </div>
                      <div class="mw-step mw-ellip" *ngIf="t.title && t.taskName">{{ t.taskName }}</div>
                    </ng-container>
                    <ng-template #plainText><span class="mw-ellip">{{ cellText(t, col) || '—' }}</span></ng-template>
                  </ng-container>
                </td>
                <td class="c-act">
                  <button class="mw-open" (click)="open(t); $event.stopPropagation()">Open</button>
                </td>
              </tr>
            </tbody>
          </table>
          </div>

          <div class="mw-foot" *ngIf="filtered().length">
            <span class="mw-showing">Showing {{ showingFrom() }}–{{ showingTo() }} of {{ filtered().length }}</span>
            <div class="mw-pager">
              <span>Rows</span>
              <select [(ngModel)]="size" (ngModelChange)="page=0">
                <option [ngValue]="8">8</option>
                <option [ngValue]="15">15</option>
                <option [ngValue]="25">25</option>
                <option [ngValue]="50">50</option>
              </select>
              <button class="mw-pg-btn" (click)="page=page-1" [disabled]="page===0" aria-label="Previous">
                <span class="material-icons">chevron_left</span>
              </button>
              <span class="mw-pg">Page {{ page + 1 }}/{{ totalPages() }}</span>
              <button class="mw-pg-btn" (click)="page=page+1" [disabled]="page >= totalPages()-1" aria-label="Next">
                <span class="material-icons">chevron_right</span>
              </button>
            </div>
          </div>
        </div>
      </main>

      <!-- R2.3 — consultation action modal: read, discuss, respond, re-consult -->
      <div class="mw-modal-overlay" *ngIf="consultModal" (click)="closeConsult()">
        <div class="mw-modal" (click)="$event.stopPropagation()">
          <div class="mw-modal-head">
            <div>
              <div class="mw-modal-title">
                <span class="material-icons">forum</span> Consultation
              </div>
              <div class="mw-modal-sub">{{ consultModal.title }} · from {{ consultModal.createdByName || consultModal.createdBy }}</div>
            </div>
            <div class="mw-modal-head-actions">
              <!-- R2.3 — a consulted user reviews the document + full record read-only,
                   then discusses/responds in the thread below. -->
              <button class="mw-modal-doc" (click)="openConsultRecord()"
                      title="Open the document and full record (read-only)">
                <span class="material-icons">description</span> View document &amp; record
              </button>
              <button class="mw-modal-x" (click)="closeConsult()" aria-label="Close">
                <span class="material-icons">close</span>
              </button>
            </div>
          </div>
          <div class="mw-modal-body">
            <div class="mw-consult-hint">
              Review the document via <b>View document &amp; record</b>, then respond in the discussion below.
            </div>
            <wf-sub-tasks-panel
              [instanceId]="consultModal.wfInstanceId"
              [canCreate]="false"
              [startExpanded]="true"
              [currentUsername]="currentUsername"
              [userRoles]="currentUserRoles"
              label="Consult">
            </wf-sub-tasks-panel>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .mw-page { height: 100vh; background: #f4f5f7; display: flex; flex-direction: column; overflow: hidden; }
    .mw-main { flex: 1; min-height: 0; max-width: none; margin: 0; padding: 18px 24px 16px; display: flex; flex-direction: column; overflow: hidden; }
    .mw-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
    .mw-title-wrap { display: flex; align-items: baseline; gap: 12px; }
    .mw-h1 { font-size: 26px; font-weight: 600; color: #1b2430; margin: 0; }
    .mw-count { font-size: 14px; color: #6b7280; }
    .mw-tab-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; padding: 0 5px; margin-left: 6px; border-radius: 999px; background: #244062; color: #fff; font-size: 11px; font-weight: 700; line-height: 1; vertical-align: middle; }
    .mw-tab.on .mw-tab-badge { background: #1b3350; }
    .mw-refresh { border: 1px solid #e2e6e3; background: #fff; border-radius: 9px; width: 38px; height: 38px; cursor: pointer; color: #4b5563; }
    .mw-refresh:hover { background: #f3f5f4; }
    .mw-tabs { display: flex; gap: 6px; margin-bottom: 14px; }
    .mw-tab { padding: 7px 16px; border-radius: 20px; font-size: 13px; cursor: pointer; border: 1px solid transparent; color: #6b7280; background: none; }
    .mw-tab.on { background: #e7f1ec; color: #16704F; border-color: #cfe3d9; font-weight: 500; }
    .mw-controls { display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
    .mw-search { position: relative; flex: 1; min-width: 220px; }
    .mw-search .material-icons { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: #9ca3af; font-size: 19px; }
    .mw-search input { width: 100%; box-sizing: border-box; padding: 10px 12px 10px 36px; border: 1px solid #dce0de; border-radius: 10px; font-size: 14px; background: #fff; }
    .mw-controls select { padding: 10px 12px; border: 1px solid #dce0de; border-radius: 10px; font-size: 14px; background: #fff; }
    .mw-loading, .mw-error { padding: 28px; text-align: center; color: #6b7280; font-size: 14px; }
    .mw-error { color: #b91c1c; }
    .mw-card { background: #fff; border: 1px solid #e8ebe9; border-radius: 14px; overflow: hidden; flex: 1; min-height: 0; display: flex; flex-direction: column; }
    .mw-scroll { flex: 1; min-height: 0; overflow: auto; }
    .mw-empty { padding: 48px; text-align: center; color: #9aa3a0; }
    .mw-empty .material-icons { font-size: 40px; color: #cbd5d0; display: block; margin: 0 auto 8px; }
    .mw-table { width: 100%; border-collapse: collapse; }
    .mw-table thead th { text-align: left; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; color: #98a1ab; padding: 9px 16px; border-bottom: 1px solid #eef0ef; background: #fbfcfb; position: sticky; top: 0; z-index: 1; }
    .mw-table tbody tr { border-bottom: 1px solid #f1f3f2; cursor: pointer; }
    .mw-table tbody tr:last-child { border-bottom: none; }
    .mw-table tbody tr:hover { background: #f7faf8; }
    .mw-table td { padding: 7px 16px; vertical-align: middle; }
    .c-wf { width: 150px; } .c-ref { width: 150px; } .c-hold { width: 110px; } .c-prio { width: 96px; } .c-age { width: 88px; } .c-act { width: 78px; text-align: right; }
    .mw-badge { font-size: 12px; padding: 4px 10px; border-radius: 8px; font-weight: 500; display: inline-flex; align-items: center; gap: 7px; max-width: 100%; box-sizing: border-box; }
    .mw-dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
    .mw-ref { font-size: 13px; font-weight: 600; color: #15604a; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
    .mw-subject-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .mw-subject { font-size: 14px; font-weight: 500; color: #1f2937; max-width: 680px; }
    .mw-consult-badge { display: inline-flex; align-items: center; gap: 3px; padding: 2px 8px; border-radius: 999px; background: #f3e8ff; color: #6d28d9; font-size: 11px; font-weight: 600; white-space: nowrap; }
    .mw-consult-badge .material-icons { font-size: 13px; }
    .mw-step { font-size: 12px; color: #8a9299; max-width: 680px; margin-top: 2px; }
    .mw-ellip { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .mw-avatar { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; background: #e7f1ec; color: #16704F; font-size: 11px; font-weight: 600; vertical-align: middle; margin-right: 7px; }
    .mw-avatar.group { background: #eae8f6; color: #4a3f9e; }
    .mw-holder-label { font-size: 12px; color: #6b7280; }
    .mw-prio { font-size: 11px; padding: 3px 11px; border-radius: 999px; font-weight: 500; white-space: nowrap; }
    .c-age { font-size: 13px; color: #6b7280; white-space: nowrap; }
    .c-age.overdue { color: #c0392b; font-weight: 600; }
    .mw-open { border: 1px solid #cfe3d9; background: #fff; border-radius: 8px; padding: 6px 14px; font-size: 12px; cursor: pointer; color: #16704F; font-weight: 500; }
    .mw-open:hover { background: #e7f1ec; }
    .mw-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 16px; border-top: 1px solid #eef0ef; flex-wrap: wrap; flex-shrink: 0; background: #fbfcfb; }
    .mw-showing { font-size: 13px; color: #6b7280; }
    .mw-pager { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #6b7280; }
    .mw-pager select { padding: 6px 9px; border: 1px solid #dce0de; border-radius: 8px; }
    .mw-pg-btn { border: 1px solid #dce0de; background: #fff; border-radius: 8px; width: 32px; height: 32px; cursor: pointer; color: #4b5563; display: inline-flex; align-items: center; justify-content: center; }
    .mw-pg-btn:disabled { color: #c8cdca; cursor: not-allowed; }
    .mw-pg { min-width: 64px; text-align: center; }
    .spin { animation: mw-spin 1s linear infinite; }
    @keyframes mw-spin { to { transform: rotate(360deg); } }

    /* R2.3 — consultation modal */
    .mw-modal-overlay { position: fixed; inset: 0; background: rgba(17,24,39,0.5); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding: 5vh 16px; overflow-y: auto; }
    .mw-modal { background: #fff; border-radius: 14px; width: 100%; max-width: min(920px, 96vw); box-shadow: 0 16px 48px rgba(0,0,0,0.28); overflow: hidden; }
    .mw-modal-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 16px 22px; border-bottom: 1px solid #e6e9e8; }
    .mw-modal-title { display: flex; align-items: center; gap: 9px; font-size: 17px; font-weight: 700; color: #111827; }
    .mw-modal-title .material-icons { color: #7C3AED; font-size: 21px; }
    .mw-modal-sub { font-size: 13px; color: #374151; margin-top: 3px; font-weight: 500; }
    .mw-modal-x { border: none; background: none; cursor: pointer; color: #4b5563; padding: 2px; }
    .mw-modal-x:hover { color: #111827; }
    .mw-modal-head-actions { display: flex; align-items: center; gap: 10px; }
    .mw-modal-doc { display: inline-flex; align-items: center; gap: 6px; border: 1px solid #1B3A6B; background: #1B3A6B; color: #fff; border-radius: 6px; padding: 6px 12px; font-size: 13px; font-weight: 600; cursor: pointer; }
    .mw-modal-doc .material-icons { font-size: 18px; }
    .mw-modal-doc:hover { background: #16305a; }
    .mw-consult-hint { font-size: 12.5px; color: #4b5563; background: #f3f6fb; border: 1px solid #dbe4f0; border-radius: 6px; padding: 8px 10px; margin-bottom: 12px; }
    .mw-modal-body { padding: 10px 22px 20px; }
  `]
})
export class WfMyWorkComponent implements OnInit {

  loading = false;
  error: string | null = null;

  tab: 'me' | 'group' | 'all' = 'all';   // combined queue (tabs removed)
  search = '';
  wfFilter = '';
  sort: 'due' | 'age' | 'prio' | 'recent' = 'recent';
  page = 0;
  size = 15;

  private items: WorkItem[] = [];
  workflowOptions: Array<{ key: string; label: string }> = [];

  private readonly PALETTE = [
    { bg: '#E6F1FB', fg: '#0C447C', dot: '#378ADD' },
    { bg: '#EEEDFE', fg: '#3C3489', dot: '#7F77DD' },
    { bg: '#FAEEDA', fg: '#633806', dot: '#EF9F27' },
    { bg: '#E1F5EE', fg: '#085041', dot: '#1D9E75' },
    { bg: '#FBEAF0', fg: '#72243E', dot: '#D4537E' },
    { bg: '#EAF3DE', fg: '#27500A', dot: '#639922' }
  ];

  // R2.3 — consultation action modal
  consultModal: WfSubTask | null = null;

  constructor(
    private taskApi: WfTaskApi,
    private subTaskApi: WfSubTaskApi,
    private auth: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  /** JWT-decoded username — the Consult panel uses it to gate complete/cancel. */
  get currentUsername(): string {
    const token = this.auth.getToken();
    if (!token) return '';
    try { return JSON.parse(atob(token.split('.')[1])).preferred_username
             || JSON.parse(atob(token.split('.')[1])).sub || ''; }
    catch { return ''; }
  }

  /** JWT realm roles — enables acting on group-assigned consultations. */
  get currentUserRoles(): string[] {
    const token = this.auth.getToken();
    if (!token) return [];
    try { return JSON.parse(atob(token.split('.')[1])).realm_access?.roles || []; }
    catch { return []; }
  }

  ngOnInit(): void {
    const t = this.route.snapshot.queryParamMap.get('tab');
    if (t === 'me' || t === 'group' || t === 'all') { this.tab = t; }
    // Config-driven columns; on any failure keep the built-in default set.
    this.taskApi.getMyTasksColumns().subscribe({
      next: cols => { if (cols && cols.length) { this.columns = cols; } },
      error: () => { /* keep default columns */ }
    });
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    forkJoin({
      mine:  this.taskApi.myTasks(0, 200),
      claim: this.taskApi.claimable([], 0, 200),
      // R2.3 — consultations assigned to me are side-tasks, not Flowable tasks;
      // best-effort so a failure here never blanks the whole inbox.
      consults: this.subTaskApi.listMine().pipe(catchError(() => of([] as WfSubTask[])))
    }).subscribe({
      next: ({ mine, claim, consults }) => {
        const map = new Map<string, WorkItem>();
        (mine?.content || []).forEach(t => map.set(t.taskId, { ...t, kind: 'me', itemType: 'task', dueMs: t.dueAt ? Date.parse(t.dueAt) : null }));
        (claim?.content || []).forEach(t => { if (!map.has(t.taskId)) { map.set(t.taskId, { ...t, kind: 'group', itemType: 'task', dueMs: t.dueAt ? Date.parse(t.dueAt) : null }); } });
        (consults || []).forEach(c => map.set('consult-' + c.id, this.consultToWorkItem(c)));
        this.items = Array.from(map.values());
        this.workflowOptions = this.buildWorkflowOptions();
        this.page = 0;
        this.loading = false;
      },
      error: err => {
        console.error('[my-work] failed to load tasks', err);
        this.error = 'Could not load your tasks. Please retry.';
        this.loading = false;
      }
    });
  }

  /** R2.3 — present a consultation (or an FYI/notification sub-task) as an inbox row. A CONSULT is a
   *  "please advise" ask; a TASK is an FYI/action notification (e.g. "Memo signed — FYI") — label it
   *  as a Notification, not a Consultation, and let its own title carry the wording. */
  private consultToWorkItem(c: WfSubTask): WorkItem {
    const ageHours = c.createdAt ? (Date.now() - Date.parse(c.createdAt)) / 3.6e6 : undefined;
    const isConsult = (c.kind || 'CONSULT').toUpperCase() === 'CONSULT';
    return {
      taskId: 'consult-' + c.id,
      // Secondary line = the task's own action label (e.g. "Print & envelop…").
      taskName: isConsult ? 'Consultation' : (c.title || 'Notification'),
      taskKey: 'consult',
      instanceId: c.wfInstanceId,
      wfKey: '',
      wfDisplayName: isConsult ? 'Consultation' : 'Notification',
      wfColor: isConsult ? '#7C3AED' : '#0d9488',
      // Ref = the instance locator (wfKey/instanceId, e.g. "outgoingMail/305"),
      // matching the breadcrumb — not the business key or the filing reference.
      businessKey: c.wfKey ? `${c.wfKey}/${c.wfInstanceId}` : (c.instanceReference || ''),
      title: c.instanceSubject || c.title,
      assignee: c.assigneeId,
      dueAt: c.deadlineAt || undefined,
      ageHours,
      overdue: !!c.deadlineAt && c.status === 'PENDING' && Date.parse(c.deadlineAt) < Date.now(),
      kind: 'me',
      itemType: 'consult',
      consult: c,
      dueMs: c.deadlineAt ? Date.parse(c.deadlineAt) : null
    };
  }

  private buildWorkflowOptions(): Array<{ key: string; label: string }> {
    const seen = new Map<string, string>();
    this.items.forEach(t => { if (t.wfKey && !seen.has(t.wfKey)) { seen.set(t.wfKey, this.wfLabel(t)); } });
    return Array.from(seen.entries()).map(([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label));
  }

  setTab(t: 'me' | 'group' | 'all'): void { this.tab = t; this.page = 0; }

  /** Count of items behind each tab, so the tabs carry a pending-work badge (a single-member role
   *  task now lands in 'me'; a multi-member role stays in 'group'). Ignores search/wf filters. */
  tabCount(t: 'me' | 'group' | 'all'): number {
    return t === 'all' ? this.items.length : this.items.filter(i => i.kind === t).length;
  }

  filtered(): WorkItem[] {
    const q = this.search.trim().toLowerCase();
    let r = this.items.filter(t => {
      // Single combined queue — assigned-to-me AND group-claimable shown together (no me/group tabs).
      if (this.wfFilter && t.wfKey !== this.wfFilter) { return false; }
      if (q && !(t.businessKey || '').toLowerCase().includes(q)
            && !this.subject(t).toLowerCase().includes(q)
            && !(t.taskName || '').toLowerCase().includes(q)) { return false; }
      return true;
    });
    r = r.slice().sort((a, b) => {
      if (this.sort === 'prio')   { return (this.prioRank(b) - this.prioRank(a)) || this.dueCmp(a, b); }
      if (this.sort === 'age')    { return (b.ageHours ?? -1) - (a.ageHours ?? -1); }        // oldest first
      if (this.sort === 'recent') { return (a.ageHours ?? 1e9) - (b.ageHours ?? 1e9); }       // newest first
      return this.dueCmp(a, b);
    });
    return r;
  }

  private dueCmp(a: WorkItem, b: WorkItem): number {
    if (a.dueMs == null && b.dueMs == null) { return 0; }
    if (a.dueMs == null) { return 1; }
    if (b.dueMs == null) { return -1; }
    return a.dueMs - b.dueMs;
  }

  pageItems(): WorkItem[] {
    const all = this.filtered();
    const start = this.page * this.size;
    return all.slice(start, start + this.size);
  }

  totalPages(): number { return Math.max(1, Math.ceil(this.filtered().length / this.size)); }
  showingFrom(): number { return this.filtered().length ? this.page * this.size + 1 : 0; }
  showingTo(): number { return Math.min(this.filtered().length, this.page * this.size + this.size); }

  subject(t: WorkItem): string { return t.title || t.taskName || '(no subject)'; }

  // ── Configurable columns (global, from GET /tasks/my/columns; default until edited) ──
  columns: MyTasksColumn[] = [
    { key: 'workflow', label: 'Workflow', render: 'workflow', source: { type: 'computed', id: 'workflow' } },
    { key: 'ref',      label: 'Ref',      render: 'ref',      source: { type: 'computed', id: 'displayRef' } },
    { key: 'subject',  label: 'Subject',  render: 'text',     source: { type: 'computed', id: 'subject' } },
    { key: 'holder',   label: 'Holder',   render: 'holder',   source: { type: 'computed', id: 'holder' } },
    { key: 'age',      label: 'Age',       render: 'age',      source: { type: 'computed', id: 'age' } }
  ];

  /** Preserve the existing column widths/styles by mapping known computed ids to their CSS class. */
  colClass(col: MyTasksColumn): string {
    switch (col.source?.id) {
      case 'workflow':   return 'c-wf';
      case 'displayRef': return 'c-ref';
      case 'subject':    return 'c-sub';
      case 'holder':     return 'c-hold';
      case 'age':        return 'c-age';
      default:           return '';
    }
  }

  isSubjectCol(col: MyTasksColumn): boolean { return col.source?.id === 'subject'; }

  /** Admins/BPMN editors get the "configure columns" affordance (route is guarded too). */
  get canConfigureColumns(): boolean {
    const roles = this.currentUserRoles.map(r => (r || '').toLowerCase());
    return roles.some(r => r === 'wf-admin' || r === 'wf-bpmn-editor' || r === 'workflow-admin' || r === 'admin');
  }

  configureColumns(): void { this.router.navigate(['/workflow-admin/my-tasks-columns']); }

  /** Resolve a cell's display text from the column source: a computed id mapped to a task
   *  field, or a formData field read from the row's resolved values. */
  cellText(t: WorkItem, col: MyTasksColumn): string {
    const src: any = col.source || {};
    // Legacy formData columns read a literal data_json key from the resolved values.
    if (src.type === 'formData') {
      const v = t.fields ? t.fields[src.fieldName] : undefined;
      return v == null ? '' : String(v);
    }
    switch (src.id) {
      case 'displayRef':  return t.businessKey || '';
      case 'subject':     return this.subject(t);
      case 'age':         return this.age(t) || '';
      case 'workflow':    return this.wfLabel(t);
      case 'stageStatus': return t.taskName || '';
      case 'holder':      return t.kind === 'me' ? 'me' : 'group';
      case 'priority':    return t.priority || '';
      default: {
        // A per-workflow "named field" (e.g. amount): the backend resolved this row's value
        // via its workflow's namedFields mapping and returned it under the id in `fields`.
        const v = t.fields ? t.fields[src.id] : undefined;
        return v == null ? '' : String(v);
      }
    }
  }

  open(t: WorkItem): void {
    if (t.itemType === 'consult' && t.consult) {
      // Open the FULL record — document + the consult question/discussion + complete are all on the
      // instance view now, so the consulted user reviews and responds in one place (no thin modal).
      // focus=consult tells the record to collapse the other rail sections and lead with the consult.
      this.router.navigate(['/workflow/r/instance', t.consult.wfInstanceId],
        { queryParams: { return: 'mywork', focus: 'consult' } });
      return;
    }
    this.router.navigate(['/workflow/r/task', t.taskId], { queryParams: { return: 'mywork' } });
  }

  /** R2.3 — close the consultation modal and refresh (a completed consult drops off). */
  closeConsult(): void {
    this.consultModal = null;
    this.load();
  }

  /**
   * R2.3 — open the consulted instance's document + full record (read-only) so the consulted user
   * can review before responding in the discussion thread. Uses the existing registry-pull instance
   * view (/workflow/r/instance/:id), which shows the document, comment thread and audit trail.
   */
  openConsultRecord(): void {
    const id = this.consultModal?.wfInstanceId;
    if (id != null) {
      this.consultModal = null;
      this.router.navigate(['/workflow/r/instance', id]);
    }
  }

  wfLabel(t: WorkItem): string {
    if (t.wfDisplayName) { return t.wfDisplayName; }
    const k = t.wfKey || '';
    const spaced = k.replace(/[-_]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
    return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase() : 'Workflow';
  }

  wfStyle(t: WorkItem): { bg: string; fg: string; dot: string } {
    if (t.wfColor) { return { bg: this.rgba(t.wfColor, 0.12), fg: t.wfColor, dot: t.wfColor }; }
    let h = 0;
    for (const ch of (t.wfKey || '')) { h = (h * 31 + ch.charCodeAt(0)) >>> 0; }
    return this.PALETTE[h % this.PALETTE.length];
  }

  private rgba(hex: string, a: number): string {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) { return hex; }
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
  }

  initials(t: WorkItem): string {
    if (t.kind === 'group') { return 'GR'; }
    const a = t.assignee || 'me';
    const parts = a.replace(/[._-]/g, ' ').trim().split(/\s+/);
    const s = parts.length >= 2 ? parts[0][0] + parts[1][0] : a.slice(0, 2);
    return s.toUpperCase();
  }

  private prioRank(t: WorkItem): number {
    const n = Number(t.priority);
    if (!isNaN(n)) { return n >= 66 ? 3 : n >= 34 ? 2 : 1; }
    const s = (t.priority || '').toLowerCase();
    if (s.startsWith('high')) { return 3; }
    if (s.startsWith('med')) { return 2; }
    if (s.startsWith('low')) { return 1; }
    return 0;
  }

  prio(t: WorkItem): { label: string; bg: string; fg: string } | null {
    const r = this.prioRank(t);
    if (r === 3) { return { label: 'High',   bg: '#FCEBEB', fg: '#791F1F' }; }
    if (r === 2) { return { label: 'Medium', bg: '#FAEEDA', fg: '#854F0B' }; }
    if (r === 1) { return { label: 'Low',    bg: '#F1EFE8', fg: '#5F5E5A' }; }
    return null;
  }

  age(t: WorkItem): string {
    const h = t.ageHours;
    if (h == null) { return ''; }
    if (h < 1)  { return '<1h'; }
    if (h < 24) { return Math.round(h) + 'h'; }
    const d = Math.floor(h / 24);
    const r = Math.round(h % 24);
    return r > 0 ? d + 'd ' + r + 'h' : d + 'd';
  }
}